"use client";

import {
  FC,
  ReactNode,
  useRef,
  useState,
} from "react";
import { Swiper } from "swiper/react";
import SwiperCore, {
  SwiperOptions,
  Grid,
  Pagination,
  Navigation,
  Autoplay,
} from "swiper";
import {
  Breakpoint,
  tailwindBreakpointsMap,
} from "apps/website/maps/Breakpoint.map";
import useClient from "apps/website/hooks/useClient";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/grid";
import IconButton from "apps/website/components/base/IconButton/IconButton";

import {
  Overflow,
  Perspective,
  overflowClassMap,
  perspectiveClassMap,
  SpaceBetween,
  spaceBetweenMap,
  NavigationStyle,
  navigationStyleMap,
  PaginationStyle,
  paginationStyleMap,
} from "./Carousel.map";
import styles from "./Carousel.module.css";

type Breakpoints = {
  [key in Breakpoint]?: number;
};

export interface ICarousel {
  slidesPerView?: number;
  slideCount?: number;
  breakpoints?: Breakpoints;
  loop?: boolean;
  overflow?: Overflow;
  perspective?: Perspective;
  centeredSlides?: boolean;
  spaceBetween?: SpaceBetween;
  showNavigation?: boolean;
  navigationStyle?: NavigationStyle;
  autoPlay?: boolean;
  autoPlayDelay?: number;
  autoPlaySpeed?: number;
  reverseAutoplay?: boolean;
  component?: string;
  pagination?: boolean;
  paginationStyle?: PaginationStyle;
  rows?: number;
  children: ReactNode;
  wrapperClassName?: string;
  allowTouchMove?: boolean;
  noSwipingSelector?: string;
  direction?: "horizontal" | "vertical"
  className?: string;
  onInitSwiper?: (swiper: SwiperCore) => void
}

const Carousel: FC<ICarousel> = ({
  slidesPerView = 1,
  slideCount = 1,
  breakpoints = { default: 1 },
  loop = false,
  overflow = "default",
  perspective = "default",
  component = "Carousel",
  centeredSlides = false,
  spaceBetween = "default",
  pagination = true,
  paginationStyle = "bullets",
  showNavigation = true,
  navigationStyle = "default",
  autoPlay = false,
  autoPlayDelay = 1,
  autoPlaySpeed = 10000,
  reverseAutoplay = false,
  rows = 1,
  children,
  wrapperClassName = "",
  className = "",
  allowTouchMove = true,
  noSwipingSelector,
  direction = "horizontal",
  onInitSwiper,
}) => {

  const swiperRef = useRef<SwiperCore>(undefined);
  const [ isStart, setIsStart ] = useState<boolean | null>(null);
  const [ isEnd, setIsEnd ] = useState<boolean | null>(null);

  const { isClient } = useClient();

  const swiperBreakpoints = (): SwiperOptions["breakpoints"] => {
    const swiperBreakpointOptions: SwiperOptions["breakpoints"] = {};
    Object.entries(breakpoints).forEach(([ key, value ]) => {
      swiperBreakpointOptions[tailwindBreakpointsMap[
        key as keyof typeof tailwindBreakpointsMap
      ]] = { slidesPerView: value, grid: { rows } };
    });

    return swiperBreakpointOptions;
  };

  const computedClasses = [
    styles.carousel,
    perspectiveClassMap[perspective],
    overflowClassMap[overflow],
    autoPlay && styles.autoplay,
    wrapperClassName,
  ].join(" ");

  const autoPlayAgs = autoPlay ? {
    delay: autoPlayDelay,
    disableOnInteraction: allowTouchMove,
  } : false;
  const paginationArgs = (pagination && showNavigation) ? paginationStyleMap[paginationStyle] : false;
  const navigationArgs = navigationStyleMap[navigationStyle];
  const speed = autoPlay ? autoPlaySpeed : 300;

  return (
    <div data-component={component} className={computedClasses} dir={reverseAutoplay ? "rtl" : "ltr"}>
      { isClient && (
        <Swiper
          direction={direction}
          className={className}
          speed={speed}
          autoplay={autoPlayAgs}
          spaceBetween={spaceBetweenMap[spaceBetween]}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
            onInitSwiper?.(swiper);
          }}
          grid={{
            rows,
            fill: "row",
          }}
          onInit={(swiper) => {
            setIsStart(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper) => {
            setIsStart(swiper.isBeginning);
            setIsEnd(swiper.isEnd);
          }}
          slidesPerView={slidesPerView}
          breakpoints={swiperBreakpoints()}
          modules={[ Grid, Pagination, Navigation, Autoplay ]}
          pagination={paginationArgs}
          loop={loop}
          centeredSlides={centeredSlides}
          loopAdditionalSlides={4}
          allowTouchMove={allowTouchMove}
          noSwipingSelector={noSwipingSelector}
        >
          { children }

          { showNavigation && (
            <>
              <div className={`absolute z-20 ${(isEnd && isStart) && "hidden"} ${navigationArgs.leftIconClassName}`}>
                <IconButton
                  icon={navigationArgs.leftIcon}
                  size={navigationArgs.iconSize}
                  iconClassName={navigationArgs.iconWrapperClassName}
                  label="Go to previous slide"
                  onClick={() => swiperRef.current?.slidePrev()}
                  disabled={!!(isStart && !loop)}
                  color={isStart && !loop ? "iconDisabled" : "default"}
                  isCarouselNav
                />
              </div>
              <div className={`absolute bottom-0 z-20 ${(isEnd && isStart) && "hidden"} ${navigationArgs.rightIconClassName}`}>
                <IconButton
                  icon={navigationArgs.rightIcon}
                  size={navigationArgs.iconSize}
                  iconClassName={navigationArgs.iconWrapperClassName}
                  label="Go to next slide"
                  onClick={() => swiperRef.current?.slideNext()}
                  disabled={!!(isEnd && !loop)}
                  color={isEnd && !loop ? "iconDisabled" : "default"}
                  isCarouselNav
                />
              </div>
              { navigationStyle === "default" && (
                <Spacer size="2xl" />
              ) }
              { (navigationStyle === "tertiary" && slideCount > 1) && (
                <Spacer size="xl" />
              ) }
            </>
          ) }
        </Swiper>
      ) }
    </div>
  );
};

export default Carousel;
